import { Component, type ErrorInfo, type ReactNode } from "react";
import * as Sentry from "@sentry/react";
import { AlertTriangle, RefreshCw } from "lucide-react";
import { Button } from "@/components/ui/button";

interface Props {
  children: ReactNode;
  fallback?: ReactNode;
  resetKey?: string;
}

interface State {
  hasError: boolean;
  error: Error | null;
}

export class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    // Stale-bundle recovery: after a deploy, the browser may still hold the
    // old index.js which references chunk hashes that no longer exist on the
    // server. The lazy import then throws "Failed to fetch dynamically
    // imported module" or similar. One forced reload fetches the new index
    // and the new chunk map. Guard with sessionStorage so we never loop.
    const msg = String(error?.message ?? "");
    const isChunkError =
      /Failed to fetch dynamically imported module/i.test(msg) ||
      /Importing a module script failed/i.test(msg) ||
      /Loading chunk \d+ failed/i.test(msg) ||
      /ChunkLoadError/i.test(error?.name ?? "");

    if (isChunkError) {
      const RELOAD_KEY = "__chunk_reload_attempted";
      const alreadyTried = sessionStorage.getItem(RELOAD_KEY);
      if (!alreadyTried) {
        sessionStorage.setItem(RELOAD_KEY, String(Date.now()));
        // Force a hard reload bypassing the HTTP cache for the navigation.
        window.location.reload();
        return;
      }
      // Reload was already attempted this session — fall through to the
      // normal fallback UI so the user isn't stuck in a reload loop.
    } else {
      // Clear the marker on any non-chunk error so future deploys can reload.
      try { sessionStorage.removeItem("__chunk_reload_attempted"); } catch {}
    }

    Sentry.captureReactException(error, errorInfo);
  }

  componentDidUpdate(prevProps: Props) {
    if (prevProps.resetKey !== this.props.resetKey && this.state.hasError) {
      this.setState({ hasError: false, error: null });
    }
  }

  handleReset = () => {
    this.setState({ hasError: false, error: null });
  };

  render() {
    if (this.state.hasError) {
      if (this.props.fallback) {
        return this.props.fallback;
      }

      return (
        <div className="flex flex-col items-center justify-center min-h-[50vh] px-6 text-center">
          <div className="w-14 h-14 rounded-full bg-destructive/10 flex items-center justify-center mb-4">
            <AlertTriangle className="h-7 w-7 text-destructive" />
          </div>
          <h2 className="font-serif text-xl font-bold mb-2">Something went wrong</h2>
          <p className="text-muted-foreground mb-6 max-w-md">
            An unexpected error occurred. Try refreshing the page or going back.
          </p>
          <div className="flex gap-3">
            <Button variant="outline" onClick={() => window.history.back()}>
              Go back
            </Button>
            <Button onClick={this.handleReset}>
              <RefreshCw className="h-4 w-4 mr-2" />
              Try again
            </Button>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}
